<script>
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ArticleContentSynonyms from "@/components/article/ArticleContentSynonyms.vue";
import { useSearchStore } from "@/stores";

import ArticleContentCombinations from "./ArticleContentCombinations.vue";
import ArticleContentEmpty from "./ArticleContentEmpty.vue";
import ArticleContentHeader from "./ArticleContentHeader.vue";
import ArticleContentTranslations from "./ArticleContentTranslations.vue";

export default defineComponent({
  name: "DictsysArticleContent",
  components: {
    ArticleContent,
    ArticleContentHeader,
    ArticleContentCollapser,
    ArticleContentCombinations,
    ArticleContentSynonyms,
    ArticleContentTranslations,
    ArticleContentEmpty
  },
  props: {
    search: {
      type: String,
      required: true
    },
    article: {
      type: Object,
      required: true
    },
    hasReadingLines: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    isCollapsible: {
      type: Boolean,
      default: false
    },
    translationsExpandedDefault: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapStores(useSearchStore),

    articleTab() {
      return this.searchStore.articleTab;
    },
    lemmas() {
      return [this.article, ...this.article.alternativeSpellings];
    },

    emptyTranslations() {
      return this.article.translations && this.article.translations.length === 0;
    },
    emptyCombinations() {
      return this.article.combinations && this.article.combinations.length === 0;
    },
    emptySynonyms() {
      return this.article.synonyms && this.article.synonyms.length === 0;
    },

    isTranslationsTab() {
      return this.articleTab === "translations" || this.articleTab === undefined;
    },
    isCombinationsTab() {
      return this.articleTab === "combinations";
    },
    isSynonymsTab() {
      return this.articleTab === "synonyms";
    },

    isCurrentTabEmpty() {
      if (this.isTranslationsTab) {
        return this.emptyTranslations;
      }

      if (this.isCombinationsTab) {
        return this.emptyCombinations;
      }

      if (this.isSynonymsTab) {
        return this.emptySynonyms;
      }

      return true;
    },
    showArticle() {
      return !this.isSynonymsTab || !this.article.hideSynonyms;
    }
  }
});
</script>

<template>
  <ArticleContent v-if="showArticle" class="article-content">
    <ArticleContentHeader :lemmas="lemmas" />

    <ArticleContentCollapser
      :is-collapsible="isCollapsible"
      :class="{
        'article-content-collapser--grey': isTranslationsTab && !emptyTranslations
      }"
    >
      <div
        class="article-content__body-container d-flex flex-column flex-lg-row"
        :class="{
          'article-content__body-container--reading-lines': hasReadingLines
        }"
      >
        <div class="indentation-container flex-grow-1 flex-shrink-1">
          <ArticleContentTranslations
            v-if="isTranslationsTab && article.translations"
            :article-id="article.id"
            :has-reading-lines="hasReadingLines"
            :translations="article.translations"
            :expanded-default="translationsExpandedDefault"
          />
          <ArticleContentCombinations
            v-if="isCombinationsTab && article.combinations"
            :article-id="article.id"
            :combinations="article.combinations"
          />
          <ArticleContentSynonyms
            v-if="isSynonymsTab && article.synonyms"
            preferred-source="dictsys"
            :synonyms="article.synonyms"
          />
          <ArticleContentEmpty
            v-if="isCurrentTabEmpty"
            :has-translations="article.hasTranslations"
            :has-combinations="article.hasCombinations"
            :has-synonyms="article.hasSynonyms"
          />
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
